import { flexRender, type ColumnDef } from "@tanstack/react-table";
import { useRouter } from "next/navigation";
import { twMerge } from "tailwind-merge";

import {
  AssignedUserFilter,
  CheckboxFilter,
  convertStringArrayToCheckboxOption,
  FilterGroup,
  FilterMenu,
} from "@/components/filters";
import { Header } from "@/components/header";
import { ImpersonationBanner } from "@/components/impersonationBanner";
import { LocationAssignmentModal } from "@/components/table/modals/location-assignment-modal";
import { TableHeader } from "@/components/table/tableHeader";
import { TableSharedWrapper } from "@/components/table/views/table-shared-wrapper";
import { useTrackAnalytics } from "@/hooks/analytics.hooks";
import { usePrefetchSlimScreenerDetailsQuery } from "@/hooks/queries/screener-builder.queries";
import type { FilterParamsHandle } from "@/hooks/use-filter-params";
import { useServerPaginatedTable } from "@/hooks/use-server-paginated-table";
import type {
  AssignedLocationsAvailableFilters,
  LocationsAvailableFilters,
  ScreenerLocation,
} from "@/types/screener-location";
import type { AllLocationsResponseScreener } from "@/types/screeners";
import { buildTestId } from "@/utils/helpers";
import { getJobNameWithDisplayName } from "@/utils/ui";

import {
  AssignedLocationsQueryKeys,
  AssignedLocationsSortableColumns,
  type AssignedLocationsTableQueryParams,
  getLocationPinningStyles,
  LocationsFilterKeys,
  LocationsQueryKeys,
  type LocationsTableQueryParams,
  LocationsTableSortableColumns,
} from "./screener-locations-columns";

interface ScreenerLocationsTableProps {
  availableFilters:
    | LocationsAvailableFilters
    | AssignedLocationsAvailableFilters;
  columns: ColumnDef<ScreenerLocation>[];
  filterParams: FilterParamsHandle<
    LocationsTableQueryParams | AssignedLocationsTableQueryParams
  >;
  isFetching: boolean;
  isLoading: boolean;
  onMyLocations?: boolean;
  screener?: AllLocationsResponseScreener;
  tableData: ScreenerLocation[];
  totalItems: number;
  totalPages: number;
}

export const ScreenerLocationsTable = ({
  availableFilters,
  columns,
  filterParams,
  isFetching,
  isLoading,
  onMyLocations = false,
  screener,
  tableData,
  totalItems,
  totalPages,
}: ScreenerLocationsTableProps) => {
  const router = useRouter();
  const track = useTrackAnalytics();

  // Prefetch the query used in the screener builder pilot hook to avoid
  // request waterfalls on entering AI builder
  usePrefetchSlimScreenerDetailsQuery(screener?.id);

  const { loadingFallback, table } = useServerPaginatedTable({
    columns,
    columnPinning: {
      left: [LocationsTableSortableColumns.LocationName],
      right: [],
    },
    data: tableData,
    isLoading,
    totalPages,
  });

  const selectedRows = table
    .getSelectedRowModel()
    .rows.map((row) => row.original);

  // Extract filter options from availableFilters
  const locationNames = availableFilters.locationNames ?? [];
  const statuses = availableFilters.statuses ?? [];
  const jobNames =
    "jobNames" in availableFilters ? availableFilters.jobNames : [];
  const users =
    "assignedToUsers" in availableFilters
      ? availableFilters.assignedToUsers
      : [];

  // Cast filterParams to base type for shared filter components
  const baseFilterParams =
    filterParams as FilterParamsHandle<LocationsTableQueryParams>;
  const assignedFilterParams =
    filterParams as FilterParamsHandle<AssignedLocationsTableQueryParams>;

  if (loadingFallback) {
    return loadingFallback;
  }

  return (
    <div className="w-full h-dvh">
      <Header />
      <div>
        <TableHeader
          breadCrumbs={[
            { href: "/", text: "All Screeners" },
            {
              text: onMyLocations
                ? "Assigned Locations"
                : getJobNameWithDisplayName(
                    screener?.jobTitle,
                    screener?.internalName,
                  ),
            },
          ]}
          editScreenerHref={
            screener ? `/screener-builder/${screener.id}/locations` : undefined
          }
        />
        <TableSharedWrapper
          filterParams={filterParams}
          filters={
            <FilterMenu<LocationsTableQueryParams>
              clearAllFilters={filterParams.clear}
              filterKeys={LocationsFilterKeys}
              filters={baseFilterParams}
              rowCount={totalItems}
              searchCountName="locations"
            >
              <FilterGroup title="Status">
                <CheckboxFilter<"status", LocationsTableQueryParams>
                  analyticsName="Location status"
                  name={LocationsQueryKeys.status}
                  options={convertStringArrayToCheckboxOption(statuses)}
                  filters={baseFilterParams}
                />
              </FilterGroup>
              <FilterGroup title="Location">
                <CheckboxFilter<"locationName", LocationsTableQueryParams>
                  analyticsName="Location name"
                  name={LocationsQueryKeys.locationName}
                  options={convertStringArrayToCheckboxOption(locationNames)}
                  filters={baseFilterParams}
                />
              </FilterGroup>
              {!onMyLocations && (
                <FilterGroup title="Assigned To">
                  <AssignedUserFilter filters={filterParams} users={users} />
                </FilterGroup>
              )}
              {onMyLocations && (
                <FilterGroup title="Job Title">
                  <CheckboxFilter<"jobName", AssignedLocationsTableQueryParams>
                    analyticsName="Job title"
                    name={AssignedLocationsQueryKeys.jobName}
                    options={convertStringArrayToCheckboxOption(jobNames)}
                    filters={assignedFilterParams}
                  />
                </FilterGroup>
              )}
            </FilterMenu>
          }
          getPinningStyles={getLocationPinningStyles}
          isLoading={isFetching}
          page={filterParams.getWithDefault(LocationsQueryKeys.page, 1)}
          setPage={(page) => filterParams.set(LocationsQueryKeys.page, page)}
          sortableColumns={Object.values(
            onMyLocations
              ? AssignedLocationsSortableColumns
              : LocationsTableSortableColumns,
          )}
          table={table}
          totalPages={totalPages}
        >
          {table.getRowModel().rows.map((row, index) => (
            <tr
              key={row.id}
              className={twMerge(
                "border-b",
                "border-uw-gray-2",
                row.original.sessionCount && [
                  "cursor-pointer",
                  "hover:brightness-95",
                ],
              )}
              data-testid={buildTestId(
                "screener-locations",
                "table",
                "row",
                index,
              )}
              onClick={() => {
                if (row.original.sessionCount) {
                  track("Position Clicked", {
                    screener_location_id: row.id,
                  });
                  router.push(`/screener-location/${row.original.id}`);
                }
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className="p-3 w-max text-sm"
                  style={{
                    ...getLocationPinningStyles(cell.column, row),
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </TableSharedWrapper>
      </div>
      <ImpersonationBanner />
      <LocationAssignmentModal
        opened={selectedRows.length > 0}
        close={() => {
          table.setRowSelection({});
        }}
        selectedRows={selectedRows}
      />
    </div>
  );
};
